import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  LayoutDashboard, Users, FileText, Newspaper, Send, Sparkles, PenLine,
  BookOpen, Layout as LayoutIcon, ScrollText, Shield, MessageCircleQuestion,
  Trophy, MessageCircle, Megaphone, ListOrdered, ChevronDown, ChevronLeft,
  LogOut, Menu, X, Search, Coins, ClipboardList, UserCog, Bell, UserX, Award, CalendarCheck,
} from 'lucide-react';
import AnnouncementModal from './AnnouncementModal';
import './AdminLayout.css';

/**
 * Modern admin panel with a right-side collapsible sidebar (RTL),
 * grouped navigation and a clean top bar. Follows the platform's
 * warm-brown palette (primary=tan, accent=deep brown).
 */
const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [perms, setPerms] = useState(null);
  const [search, setSearch] = useState('');
  const [inactivePending, setInactivePending] = useState(0);

  const role = user?.admin_role || '';
  const isMaster = role === 'مطلق';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/my-permissions');
        setPerms(new Set(data?.permissions || []));
      } catch {
        setPerms(new Set());
      }
    })();
  }, []);

  // Load reminder badge (uncontacted + due-for-follow-up) — refresh every 3 min.
  useEffect(() => {
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const { data } = await api.get('/admin/inactive-readers/summary');
        if (!cancelled) setInactivePending(Number(data?.pending_total) || 0);
      } catch { /* silent */ }
    };
    fetchPending();
    const t = setInterval(fetchPending, 180000);
    return () => { cancelled = true; clearInterval(t); };
  }, [location.pathname]);

  const has = (id) => isMaster || (perms && perms.has(id));

  // Navigation groups — each item declares its permission `id` (matches backend).
  const groups = useMemo(() => [
    {
      id: 'main',
      label: null,
      items: [
        { id: 'dashboard', label: 'الرئيسة', icon: LayoutDashboard, path: '/admin', always: true },
      ],
    },
    {
      id: 'readers',
      label: 'القراء والتقارير',
      icon: Users,
      items: [
        { id: 'readers', label: 'إدارة القراء', icon: Users, path: '/admin/readers' },
        { id: 'reports', label: 'التقارير القرائية', icon: ClipboardList, path: '/admin/reports' },
        { id: 'points', label: 'النقاط والإنجازات', icon: Coins, path: '/admin/points' },
        { id: 'streaks', label: 'المواصلة القرائية', icon: Trophy, path: '/admin/streaks' },
        { id: 'badges', label: 'إدارة الأوسمة', icon: Award, path: '/admin/badges', always: true },
        { id: 'badges-progress', label: 'تقدّم الأوسمة', icon: Award, path: '/admin/badges/progress', always: true },
        { id: 'badges-digest', label: 'الملخص اليومي للأوسمة', icon: CalendarCheck, path: '/admin/badges/digest', always: true },
        { id: 'inactive', label: 'الحسابات الخاملة', icon: UserX, path: '/admin/inactive', always: true, badgeSource: 'inactivePending' },
        { id: 'registrations', label: 'طلبات التسجيل', icon: UserCog, path: '/admin/registrations' },
      ],
    },
    {
      id: 'content',
      label: 'المحتوى',
      icon: Newspaper,
      items: [
        { id: 'articles', label: 'إدارة المقالات', icon: Newspaper, path: '/admin/articles' },
        { id: 'comments', label: 'إدارة التعليقات', icon: MessageCircle, path: '/admin/comments' },
        { id: 'quotes', label: 'العبارات التشجيعية', icon: Sparkles, path: '/admin/quotes' },
        { id: 'productions', label: 'مراجعة النتاجات', icon: PenLine, path: '/admin/productions' },
        { id: 'announcements', label: 'إدارة الإعلانات', icon: Megaphone, path: '/admin/announcements' },
      ],
    },
    {
      id: 'courses',
      label: 'المساقات',
      icon: BookOpen,
      items: [
        { id: 'courses', label: 'إدارة المساقات', icon: BookOpen, path: '/admin/courses' },
        { id: 'enrollments', label: 'الاشتراكات', icon: ClipboardList, path: '/admin/enrollments' },
        { id: 'questions', label: 'أسئلة المساقات', icon: MessageCircleQuestion, path: '/admin/questions' },
      ],
    },
    {
      id: 'engagement',
      label: 'التواصل',
      icon: Send,
      items: [
        { id: 'notifications', label: 'إرسال إشعارات', icon: Send, path: '/admin/notifications' },
        { id: 'templates', label: 'قوالب الرسائل', icon: FileText, path: '/admin/templates' },
      ],
    },
    {
      id: 'system',
      label: 'الإعدادات والنظام',
      icon: Shield,
      items: [
        { id: 'landing', label: 'صفحة الهبوط', icon: LayoutIcon, path: '/admin/landing' },
        { id: 'course-celebration', label: 'نص احتفال ختم المساق', icon: Sparkles, path: '/admin/course-celebration' },
        { id: 'registration-schema', label: 'استمارة التسجيل', icon: LayoutIcon, path: '/admin/registration-schema' },
        { id: 'dropdown', label: 'القوائم المنسدلة', icon: ListOrdered, path: '/admin/dropdown' },
        { id: 'historical-records', label: 'السجل التاريخي', icon: ScrollText, path: '/admin/historical-records' },
        { id: 'permissions', label: 'إدارة الصلاحيات', icon: Shield, path: '/admin/permissions' },
        { id: 'admin-accounts', label: 'الحسابات الإدارية', icon: UserCog, path: '/admin/admin-accounts' },
      ],
    },
  ], []);

  // Auto-open the group that contains the current active route.
  // Also close mobile drawer when the route changes.
  useEffect(() => {
    const active = groups.find(g => g.items.some(i => location.pathname === i.path));
    if (active && !openGroups[active.id]) {
      setOpenGroups(prev => ({ ...prev, [active.id]: true }));
    }
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const toggleGroup = (gid) => setOpenGroups(prev => ({ ...prev, [gid]: !prev[gid] }));

  const filteredGroups = useMemo(() => {
    const q = search.trim();
    return groups
      .map(g => ({
        ...g,
        items: g.items.filter(it => (it.always || has(it.id)) && (!q || it.label.includes(q))),
      }))
      .filter(g => g.items.length > 0);
  }, [groups, perms, search, isMaster]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const roleLabel = role === 'مطلق' ? 'مدير مطلق' : role === 'عضو إداري' ? 'عضو إداري' : role;

  const renderSidebar = () => (
    <aside
      className={`admin-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
      data-testid="admin-sidebar"
    >
      <div className="sidebar-header">
        <Link to="/admin" className="sidebar-brand">
          <img
            src="https://customer-assets.emergentagent.com/job_anwar-reading/artifacts/9ouhjo98_%D8%A7%D9%84%D8%B4%D8%B9%D8%A7%D8%B1%20%D9%81%D9%82%D8%B7.svg"
            alt="غصن الزيتون"
            className="brand-logo"
          />
          {!collapsed && (
            <div className="brand-text">
              <span className="brand-title">لوحة الإدارة</span>
              <span className="brand-sub">{roleLabel}</span>
            </div>
          )}
        </Link>
        <button
          className="sidebar-collapse-btn"
          onClick={() => setCollapsed(v => !v)}
          data-testid="sidebar-collapse-btn"
          title={collapsed ? 'توسيع' : 'طي'}
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? '-rotate-180' : ''}`} />
        </button>
      </div>

      {!collapsed && (
        <div className="sidebar-search">
          <Search className="w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث في القوائم..."
            data-testid="sidebar-search"
          />
        </div>
      )}

      <nav className="sidebar-nav">
        {filteredGroups.map(g => {
          // Solo item (main dashboard) — render as a top-level link.
          if (!g.label) {
            return g.items.map(it => {
              const Icon = it.icon;
              const active = location.pathname === it.path;
              return (
                <Link
                  key={it.id}
                  to={it.path}
                  className={`nav-link solo ${active ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${it.id}`}
                  title={collapsed ? it.label : undefined}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {!collapsed && <span className="nav-label">{it.label}</span>}
                </Link>
              );
            });
          }
          const isOpen = !!openGroups[g.id];
          const GIcon = g.icon;
          const hasActive = g.items.some(i => location.pathname === i.path);
          return (
            <div key={g.id} className={`nav-group ${hasActive ? 'has-active' : ''}`}>
              {!collapsed && (
                <button
                  className={`nav-group-header ${isOpen ? 'open' : ''}`}
                  onClick={() => toggleGroup(g.id)}
                  data-testid={`group-${g.id}`}
                >
                  <GIcon className="w-4 h-4" />
                  <span className="nav-group-label">{g.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 chevron ${isOpen ? 'rotate' : ''}`} />
                </button>
              )}
              {collapsed && (
                <div className="collapsed-group-icon" title={g.label}>
                  <GIcon className="w-5 h-5" />
                </div>
              )}
              <div className={`nav-group-items ${collapsed || isOpen ? 'visible' : ''}`}>
                {g.items.map(it => {
                  const Icon = it.icon;
                  const active = location.pathname === it.path;
                  const badge = it.badgeSource === 'inactivePending' ? inactivePending : 0;
                  return (
                    <Link
                      key={it.id}
                      to={it.path}
                      className={`nav-link ${active ? 'active' : ''}`}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${it.id}`}
                      title={collapsed ? it.label : undefined}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {!collapsed && <span className="nav-label">{it.label}</span>}
                      {badge > 0 && (
                        <span
                          className={`nav-badge ${collapsed ? 'nav-badge-dot' : ''}`}
                          data-testid={`nav-badge-${it.id}`}
                          title={`${badge} حساب بحاجة إلى متابعة`}
                        >
                          {collapsed ? '' : (badge > 99 ? '99+' : badge)}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button
          onClick={handleLogout}
          className="footer-btn danger"
          data-testid="admin-logout-btn"
          title={collapsed ? 'تسجيل الخروج' : undefined}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!collapsed && <span>تسجيل الخروج</span>}
        </button>
      </div>
    </aside>
  );

  return (
    <div className={`admin-shell ${collapsed ? 'shell-collapsed' : ''}`}>
      {renderSidebar()}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <div className="admin-main">
        <header className="admin-topbar">
          <button
            className={`mobile-toggle ${mobileOpen ? 'is-open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            data-testid="mobile-sidebar-toggle"
            aria-label={mobileOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <div className="topbar-search">
            <Search className="w-4 h-4" />
            <input
              placeholder="ابحث سريعاً..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="topbar-search"
            />
          </div>

          <div className="topbar-user">
            <Link to="/admin" className="topbar-icon-btn" title="الإشعارات">
              <Bell className="w-4 h-4" />
            </Link>
            <div className="topbar-avatar">
              <div className="avatar-circle">{(user?.name || '?').slice(0, 1)}</div>
              <div className="avatar-info">
                <span className="avatar-name">{user?.name || '—'}</span>
                <span className="avatar-role">{roleLabel}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="admin-content">{children}</main>
      </div>

      <AnnouncementModal />
    </div>
  );
};

export default AdminLayout;
